/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, Injector, NgModule } from '@angular/core';
import { LAYOUT_CONFIG } from '@angular/flex-layout';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxsModule } from '@ngxs/store';
import { PolygonSelectModule } from './modules/map/command/polygon-select/polygon-select.module';
import { SearchModule } from './modules/search/search.module';
import { UiModule } from './core/ui/ui.module';
import { SecurityModule } from './core/security/security.module';
import { PipeModule } from './modules/shared/pipes/pipe.module';
import { ReportModule } from './modules/reports/report.module';
import { AuthState } from './core/security/state/authState.state';
import { SearchState } from './modules/search/state/search.state';
import { env } from '../environments/environment-factory';
import { APP_BASE_HREF } from '@angular/common';
import { RootComponent } from './core/root/root.component';
import { NgxsStoragePluginModule, STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { ErrorHttpInterceptor } from './core/security/interceptors/error.httpInterceptor';
import { AppRouterModule } from './core/routing/app-router.module';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { PageNotFoundComponent } from './core/root/page-not-found.component';
import { AppConfigState } from './core/state/appConfig/app-config.state';
import { NotificationState } from './core/state/notification/notification.state';
import { PrintOptionsState } from './modules/map/command/print/state/print-options.state';
import { DataFormState } from './modules/reports/state/dataForm.state';
import { ReportState } from './modules/reports/state/report.state';
import { NgxsSnapshotPluginModule } from './ngxs/snapshotPlugin/ngxs-snapshot-plugin.module';
import { ThemeSwitcherModule } from './core/ui/theme-switcher/theme-switcher.module';
import { ThemeState } from './core/ui/theme-switcher/state/theme.state';
import { FileDownloadModule } from './elements/fileDownload/file-download.module';
import { DataPrintModule } from './components/dataPrint/data-print.module';
import { DigitizePointReportModule } from './elements/digitizePointReport/digitize-point-report.module';
import { ReportEditButtonModule } from './elements/reportButtons/report-edit-button.module';
import { ReportButtonModule } from './elements/reportButtons/report-button.module';
import {
    SelectDataReport,
    SelectDataReportByName,
    SelectFeatureReport,
    SelectFeatureReportByName,
    SetActiveDataReports,
    SetActiveFeatureReports
} from './modules/reports/state/report.actions';
import { DashGisErrorHandler } from './modules/shared/dash-gis-error-handler.service';
import { ZoomButtonElementModule } from './elements/zoomButton/zoom-button-element.module';
import { SharedModule } from './modules/shared/shared.module';
import { MapConfigState } from './modules/map/state/map-config.state';
import { LiveSearchService } from './modules/shared/services/liveSearch.service';
import { GsptService } from './components/gspt/gspt.service';
import { RedlineService } from './components/redline/services/redline.service';
import { IconLoaderService } from './modules/shared/services/iconLoader.service';
import { MeasureService } from './modules/shared/services/measure.service';
import { MapInteractionsService } from './modules/map/services/mapInteractions.service';
import { ConsoleLoggerService } from './modules/shared/services/consoleLogger.service';
import { DeviceService } from './modules/shared/services/device.service';
import { DigitizePointService } from './modules/map/command/digitize-point/digitize-point.service';
import { LineIsolationService } from './components/isolation/line-isolation.service';
import { DownloadService } from './modules/shared/services/download.service';
import { DigitizePointConfigService } from './modules/map/command/digitize-point/digitize-point-config.service';
import { DataFormService } from './modules/reports/services/dataform.service';
import { ReportService } from './modules/reports/services/report.service';
import { QueryService } from './modules/map/services/query.service';
import { SearchService } from './modules/search/services/search.service';
import { GoogleMapService } from './modules/map/services/googleMap.service';
import { LoggerService } from './modules/shared/services/logger.service';
import { RestService } from './modules/shared/services/rest.service';
import { MapConfigService } from './modules/map/services/mapConfig.service';
import { MapService } from './modules/map/services/map.service';
import { RedlineConfigService } from './components/redline/services/redline-config.service';
import { ActiveMapState } from './modules/map/state/active-map.state';
import { SelectionState } from './modules/map/state/selection.state';
import { MapModule } from './modules/map/map.module';
import { RedlineState } from './components/redline/state/redline.state';
import { RedlineMarkupState } from './components/redlineMarkup/state/redlineMarkup.state';
import { LogoutModule } from './core/security/components/logout/logout.module';
import { LocationService } from './modules/map/services/location.service';
import { LocalStorageProxy } from './modules/shared/local-storage-shim';
import { DigitizePointConfigState } from './modules/map/command/digitize-point/state/digitize-point-config.state';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { ActiveToolState } from './modules/map/toolbar/active-tool/state/active-tool.state';
import { PolygonSelectState } from './modules/map/command/polygon-select/state/polygon-select.state';
import { NgxsResetPluginModule } from './ngxs/resetPlugin/ngxs-reset.module';
import { LoggedOut } from './core/security/state/authState.actions';
import { ReportDisplayService } from './modules/reports/services/report-display.service';
import { UploaderState } from './modules/reports/interface/reportControls/upload/state/upload.state';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { PageState } from './core/state/page/page.state';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { FeatureService } from './modules/map/services/feature.service';
import { DocumentLinkModule } from './elements/documentLink/document-link.module';
import { ZoomService } from './modules/shared/services/zoom.service';
import { OpenIdState } from './core/security/state/openIdState.state';
import { QrCodeService } from './modules/shared/services/qr-code.service';
import { QrCodeModule } from './elements/qr-code/qr-code.module';
import { FetchModule } from './modules/shared/httpFetch/fetch.module';
import { UserToolsModule } from './core/security/components/user-tools/user-tools.module';
import { ResetPasswordModule } from './core/security/components/password/reset-password.module';
import { ForcePasswordChangeInterceptor } from './core/security/interceptors/force-password-change.interceptor';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { DashGisNg2ResizableModule } from './core/ui/dash-gis-ng2-resizable/dash-gis-ng2-resizable.module';

@NgModule({
    bootstrap: [ RootComponent ],
    declarations: [ RootComponent, PageNotFoundComponent ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,

        // routing
        AppRouterModule,

        // app modules
        SharedModule,
        MapModule,
        SecurityModule,
        ReportModule,
        DataPrintModule,
        SearchModule,
        UiModule,
        PolygonSelectModule,
        ThemeSwitcherModule,
        PipeModule,
        DashGisNg2ResizableModule,
        LogoutModule,
        ResetPasswordModule,
        UserToolsModule,

        // custom elements
        FileDownloadModule,
        DigitizePointReportModule,
        DocumentLinkModule,
        QrCodeModule,
        ReportButtonModule,
        ReportEditButtonModule,
        ZoomButtonElementModule,

        // fetch
        FetchModule.forRoot(),

        // store
        NgxsModule.forRoot(
            [
                ActiveToolState,
                RedlineState,
                RedlineMarkupState,
                PolygonSelectState,
                DigitizePointConfigState,
                NotificationState,
                AuthState,
                OpenIdState,
                PrintOptionsState,
                ReportState,
                DataFormState,
                SearchState,
                ThemeState,
                UploaderState,
                SelectionState,
                ActiveMapState,
                MapConfigState,
                PageState,
                AppConfigState
            ],
            {
                developmentMode: !env.production,
                selectorOptions: {
                    // defaults for both will change to false in Ngxs v4
                    injectContainerState: true,
                    suppressErrors: true
                }
            }
        ),
        NgxsStoragePluginModule.forRoot({
            key: [
                'auth.token',
                'auth.refreshToken',
                'auth.requestedRoute',
                'auth.activeMethod',
                'auth.needsDisclaimer',
                'printOptions',
                'theme.variant'
            ]
        }),
        NgxsFormPluginModule.forRoot(),
        NgxsRouterPluginModule.forRoot(),
        NgxsSnapshotPluginModule.forRoot({
            groups: [
                {
                    name: 'featureReports',
                    triggers: [ SetActiveFeatureReports, SelectFeatureReport, SelectFeatureReportByName ],
                    keys: [
                        { key: 'reports.activeFeatureReports', required: true },
                        { key: 'reports.selectedFeatureReports' },
                        { key: 'reports.featureReportKeys' },
                        { key: 'reports.options' }
                    ]
                },
                {
                    name: 'dataReports',
                    triggers: [ SetActiveDataReports, SelectDataReport, SelectDataReportByName ],
                    keys: [
                        { key: 'reports.activeDataReports', required: true },
                        { key: 'reports.selectedDataReport' },
                        { key: 'reports.options' }
                    ]
                }
            ]
        }),
        NgxsResetPluginModule.forRoot({
            trigger: LoggedOut,
            preserve: [
                'appConfig',
                'auth.loaded',
                'auth.needsDisclaimer',
                'auth.methods',
                'auth.isInitialVisit',
                'openid.config',
                'openid.buttonText',
                'map.googleInitialized',
            ]
        }),
        NgxsReduxDevtoolsPluginModule.forRoot({ disabled: env.production }),

        NgxsLoggerPluginModule.forRoot({
            collapsed: false,
            disabled: env.production || true
        })
    ],
    providers: [
        { provide: ErrorHandler, useClass: DashGisErrorHandler },
        // Native ng2 services
        DataFormService,
        DeviceService,
        DigitizePointConfigService,
        DigitizePointService,
        DownloadService,
        FeatureService,
        GoogleMapService,
        GsptService,
        IconLoaderService,
        LineIsolationService,
        LiveSearchService,
        LocationService,
        MapConfigService,
        MapInteractionsService,
        MapService,
        MeasureService,
        QrCodeService,
        QueryService,
        RedlineConfigService,
        RedlineService,
        ReportDisplayService,
        ReportService,
        RestService,
        SearchService,
        ZoomService,
        { provide: APP_BASE_HREF, useValue: env.baseHref },
        { provide: LoggerService, useClass: ConsoleLoggerService },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorHttpInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ForcePasswordChangeInterceptor, multi: true },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'standard' }},
        {
            provide: LAYOUT_CONFIG,
            useValue: {
                addFlexToParent: false,
                addOrientationBps: true,
                disableDefaultBps: false,
                disableVendorPrefixes: false,
                serverLoaded: false,
                useColumnBasisZero: false
            }
        },
        { provide: STORAGE_ENGINE, useClass: LocalStorageProxy, deps: [ Injector ] }
    ]
})
export class DashGisModule {}
